import { useState, useEffect } from "react";
import { FarmlyPlace } from "@/lib/types";
import { getPlaceBySlug, PlacesDataSource } from "@/data/places/repository";

export type UsePlaceResult = {
  place: FarmlyPlace | null;
  loading: boolean;
  error?: string;
  source: PlacesDataSource | null;
};

/**
 * Hook for fetching a single place by slug.
 * Handles loading state, errors, and exposes the active data source.
 */
export function usePlace(slug: string): UsePlaceResult {
  const [place, setPlace] = useState<FarmlyPlace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  const [source, setSource] = useState<PlacesDataSource | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchData() {
      if (!slug) {
        setLoading(false);
        setPlace(null);
        return;
      }

      setLoading(true);
      setError(undefined);

      try {
        const result = await getPlaceBySlug(slug);

        if (!cancelled) {
          setPlace(result.place);
          setSource(result.source);
          setError(result.error);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("usePlace error:", err);
          setError("Ett oväntat fel uppstod.");
          setPlace(null);
          setSource("mock");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  return { place, loading, error, source };
}
